import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DocumentTemplateSelector from './DocumentTemplateSelector';
import { apiFetch } from './utils/api';
import { generateDocumentOutput } from './utils/documentGeneration';

function PPMContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [contract, setContract] = useState(null);
  const [formData, setFormData] = useState({
    contract_ref: '',
    contract_name: '',
    customer_id: '',
    site_id: '',
    start_date: '',
    contract_term_years: 1,
    total_annual_value: '',
    discount_percent: 0,
    payment_terms: 'annual_upfront',
    status: 'active',
    notes: ''
  });
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [discountTiers, setDiscountTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    fetchCustomers();
    fetchSites();
    fetchPaymentTerms();
    fetchDiscountTiers();
    if (!isNew) {
      fetchContract();
    } else {
      generateContractNumber();
      setLoading(false);
    }
  }, [id]);

  // Filter sites when customer changes
  useEffect(() => {
    if (formData.customer_id) {
      const customerSites = sites.filter(s => s.customer_id === parseInt(formData.customer_id));
      setFilteredSites(customerSites);
      // Reset site selection if current site doesn't belong to new customer
      if (formData.site_id && !customerSites.find(s => s.id === parseInt(formData.site_id))) {
        setFormData(prev => ({ ...prev, site_id: '' }));
      }
    } else {
      setFilteredSites([]);
      setFormData(prev => ({ ...prev, site_id: '' }));
    }
  }, [formData.customer_id, sites]);

  const generateContractNumber = async () => {
    try {
      const response = await apiFetch('/api/ppm-config/generate-number', {
        method: 'POST'
      });
      const data = await response.json();
      setFormData(prev => ({ ...prev, contract_ref: data.contract_number }));
    } catch (err) {
      console.error('Error generating contract number:', err);
      // Fall back to manual entry if generation fails
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await apiFetch('/api/customers');
      const data = await response.json();
      setCustomers(data);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await apiFetch('/api/sites');
      const data = await response.json();
      setSites(data);
    } catch (err) {
      console.error('Error fetching sites:', err);
    }
  };

  const fetchPaymentTerms = async () => {
    try {
      const response = await apiFetch('/api/custom-lists?list_type=ppm_payment_terms');
      const data = await response.json();
      setPaymentTerms(data);
    } catch (err) {
      console.error('Error fetching payment terms:', err);
    }
  };

  const fetchDiscountTiers = async () => {
    try {
      const response = await apiFetch('/api/ppm/contracts/discount-tiers');
      const data = await response.json();
      setDiscountTiers(data);
    } catch (err) {
      console.error('Error fetching discount tiers:', err);
    }
  };

  const handleTermChange = (termYears) => {
    setFormData(prev => ({ ...prev, contract_term_years: parseInt(termYears) }));

    // Auto-suggest discount based on term
    const tier = discountTiers.find(t => t.term_years === parseInt(termYears));
    if (tier) {
      setFormData(prev => ({ ...prev, discount_percent: tier.discount_percent }));
    }
  };

  // Calculate end date based on start date + term
  const calculateEndDate = () => {
    if (!formData.start_date || !formData.contract_term_years) return '';
    const start = new Date(formData.start_date);
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + formData.contract_term_years);
    return end.toISOString().split('T')[0];
  };

  // Calculate total contract value
  const calculateTotalValue = () => {
    const annual = parseFloat(formData.total_annual_value) || 0;
    const years = parseInt(formData.contract_term_years) || 1;
    const discount = parseFloat(formData.discount_percent) || 0;
    return (annual * years * (1 - discount / 100)).toFixed(2);
  };

  const fetchContract = async () => {
    try {
      const response = await apiFetch(`/api/ppm/contracts/${id}`);
      const data = await response.json();
      setContract(data);
      setFormData({
        contract_ref: data.contract_ref || '',
        contract_name: data.contract_name || '',
        customer_id: data.customer_id || '',
        site_id: data.site_id || '',
        start_date: data.start_date ? data.start_date.split('T')[0] : '',
        contract_term_years: data.contract_term_years || 1,
        total_annual_value: data.total_annual_value || '',
        discount_percent: data.discount_percent || 0,
        payment_terms: data.payment_terms || 'annual_upfront',
        status: data.status || 'active',
        notes: data.notes || ''
      });
      setLoading(false);
    } catch (err) {
      console.error('Error fetching contract:', err);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.contract_ref || !formData.customer_id || !formData.site_id || !formData.start_date) {
      alert('Please fill in all required fields (Contract Ref, Customer, Site, Start Date)');
      return;
    }

    setSaving(true);
    setSaved(false);

    try {
      const url = isNew
        ? '/api/ppm/contracts'
        : `/api/ppm/contracts/${id}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save contract');

      const savedData = await response.json();

      if (isNew) {
        navigate(`/ppm-contracts/${savedData.id}`);
      } else {
        setContract(savedData);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      console.error('Error saving contract:', err);
      alert('Failed to save contract: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to cancel this contract? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiFetch(`/api/ppm/contracts/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to cancel contract');

      alert('Contract cancelled successfully');
      navigate('/ppm-contracts');
    } catch (err) {
      console.error('Error cancelling contract:', err);
      alert('Failed to cancel contract: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading contract...</div>
      </div>
    );
  }

  const selectedCustomer = customers.find(c => c.id === parseInt(formData.customer_id));
  const selectedSite = sites.find(s => s.id === parseInt(formData.site_id));

  const prepareContractData = async (contract) => {
    const customer = customers.find(c => c.id === parseInt(contract.customer_id));
    const site = sites.find(s => s.id === parseInt(contract.site_id));

    return {
      contract_ref: contract.contract_ref,
      contract_name: contract.contract_name || '',
      date: new Date().toLocaleDateString('en-GB'),
      customer_name: customer?.company_name || '',
      customer_address: customer?.address || '',
      customer_postcode: customer?.postcode || '',
      site_name: site?.site_name || '',
      site_address: site?.address || '',
      site_postcode: site?.postcode || '',
      start_date: contract.start_date ? new Date(contract.start_date).toLocaleDateString('en-GB') : '',
      end_date: calculateEndDate() ? new Date(calculateEndDate()).toLocaleDateString('en-GB') : '',
      contract_term_years: contract.contract_term_years || 1,
      total_annual_value: contract.total_annual_value || 0,
      discount_percent: contract.discount_percent || 0,
      payment_terms: contract.payment_terms || '',
      status: contract.status || 'active',
      notes: contract.notes || ''
    };
  };

  const handlePrint = async () => {
    if (!selectedTemplate) {
      setShowTemplateSelector(true);
      return;
    }
    const outputWindow = window.open('', '_blank');
    if (outputWindow) {
      outputWindow.document.write('<p style="font-family: sans-serif; padding: 20px;">Preparing document...</p>');
    }
    await handleTemplateGenerate(selectedTemplate, 'print', null, outputWindow);
  };

  const handleDownloadPDF = async () => {
    if (!selectedTemplate) {
      setShowTemplateSelector(true);
      return;
    }
    await handleTemplateGenerate(selectedTemplate, 'pdf');
  };

  const handleTemplateGenerate = async (template, action, data, outputWindow = null) => {
    try {
      if (action === 'email') {
        setSelectedTemplate(template);
        setShowTemplateSelector(false);
        handleEmailClick();
        return;
      }

      const templateData = await prepareContractData(formData);
      setSelectedTemplate(template);
      await generateDocumentOutput(template, action, templateData, `PPM-Contract-${formData.contract_ref || id}`, outputWindow);
      setShowTemplateSelector(false);
    } catch (error) {
      console.error('Document generation error:', error);
      alert('Error generating document: ' + error.message);
    }
  };

  const handleEmailClick = () => {
    setEmailTo(selectedCustomer?.email || '');
    setEmailSubject(`PPM Contract ${formData.contract_ref}`);
    setEmailBody(`Dear ${selectedCustomer?.company_name},\n\nPlease find attached your PPM Contract.\n\nBest regards`);
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailTo) {
      alert('Please enter recipient email');
      return;
    }

    setSendingEmail(true);
    try {
      const response = await apiFetch(`/api/ppm-contracts/${id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emailTo,
          subject: emailSubject,
          body: emailBody
        })
      });

      if (response.ok) {
        alert('Email sent successfully!');
        setShowEmailModal(false);
      } else {
        alert('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error sending email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/ppm-contracts')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">
                {isNew ? 'New PPM Contract' : formData.contract_ref || 'PPM Contract'}
              </h1>
              {selectedCustomer && (
                <p className="text-sm text-blue-100 mt-1">
                  {selectedCustomer.company_name}
                  {selectedSite && ` → ${selectedSite.site_name}`}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!isNew && (
                <>
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition text-sm shadow-sm"
                  >
                    🗑️ Delete
                  </button>
                  <button
                    onClick={() => setShowTemplateSelector(true)}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
                    title={selectedTemplate ? `Template: ${selectedTemplate.name}` : 'Select Template'}
                  >
                    📋 {selectedTemplate ? selectedTemplate.name : 'Template'}
                  </button>
                  <button
                    onClick={handlePrint}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
                  >
                    🖨️ Print
                  </button>
                  <button
                    onClick={handleDownloadPDF}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
                  >
                    📄 PDF
                  </button>
                  <button
                    onClick={handleEmailClick}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
                  >
                    📧 Email
                  </button>
                </>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
              >
                💾 {saving ? 'Saving...' : 'Save'}
              </button>
              {saved && (
                <span className="text-sm text-white font-medium">✓ Saved</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1600px] mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Content - 2/3 width */}
          <div className="lg:col-span-2 space-y-4">

            {/* Section 1: Contract Details */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
                Contract Details
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contract Reference *
                  </label>
                  <input
                    type="text"
                    value={formData.contract_ref}
                    onChange={(e) => setFormData({ ...formData, contract_ref: e.target.value })}
                    disabled={!isNew}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    placeholder="PPM-2026-001"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {isNew ? 'Auto-generated. Configure format in Settings → PPM Contracts' : 'Cannot be changed after creation'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contract Name
                  </label>
                  <input
                    type="text"
                    value={formData.contract_name}
                    onChange={(e) => setFormData({ ...formData, contract_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Fire Door Maintenance"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer *
                  </label>
                  <select
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Customer</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.company_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site *
                  </label>
                  <select
                    value={formData.site_id}
                    onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}
                    disabled={!formData.customer_id}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  >
                    <option value="">
                      {formData.customer_id ? 'Select Site' : 'Select Customer First'}
                    </option>
                    {filteredSites.map(site => (
                      <option key={site.id} value={site.id}>
                        {site.site_name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Equipment will be added from this site
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Terms
                  </label>
                  <select
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Payment Terms</option>
                    {paymentTerms.map(term => (
                      <option key={term.id} value={term.list_value}>
                        {term.list_value}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Configure payment terms in Settings → List Creation
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contract Term *
                  </label>
                  <select
                    value={formData.contract_term_years}
                    onChange={(e) => handleTermChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="1">1 Year</option>
                    <option value="2">2 Years</option>
                    <option value="3">3 Years</option>
                    <option value="5">5 Years</option>
                  </select>
                  {formData.start_date && (
                    <p className="text-xs text-gray-500 mt-1">
                      End date: {calculateEndDate()}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Annual Value
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.total_annual_value}
                    onChange={(e) => setFormData({ ...formData, total_annual_value: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Value per year
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount %
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.discount_percent}
                    onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {discountTiers.find(t => t.term_years === formData.contract_term_years) ?
                      `Suggested: ${discountTiers.find(t => t.term_years === formData.contract_term_years).discount_percent}%` :
                      'Override suggested discount'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  rows="4"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Contract notes and special terms..."
                />
              </div>
            </div>

            {/* Section 2: Equipment (Phase 2) */}
            {!isNew && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
                  Equipment
                </h2>
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">Equipment management coming in Phase 2</p>
                  <p className="text-xs mt-1">Add equipment items and set service frequencies</p>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - 1/3 width - Sticky */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-4">

              {/* Contract Value Breakdown */}
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Contract Value</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between pb-2">
                    <span className="text-gray-600">Annual Value:</span>
                    <span className="font-medium">
                      £{parseFloat(formData.total_annual_value || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between pb-2">
                    <span className="text-gray-600">Contract Term:</span>
                    <span className="font-medium">
                      {formData.contract_term_years} {formData.contract_term_years === 1 ? 'Year' : 'Years'}
                    </span>
                  </div>
                  <div className="flex justify-between pb-2">
                    <span className="text-gray-600">Discount:</span>
                    <span className={`font-medium ${formData.discount_percent > 0 ? 'text-green-600' : ''}`}>
                      {formData.discount_percent}%
                    </span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-900 font-semibold">Total Contract Value:</span>
                      <span className="text-lg font-bold text-blue-600">
                        £{calculateTotalValue()}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Over {formData.contract_term_years} {formData.contract_term_years === 1 ? 'year' : 'years'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Summary Card */}
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`font-medium ${
                      formData.status === 'active' ? 'text-green-600' :
                      formData.status === 'draft' ? 'text-gray-600' :
                      'text-red-600'
                    }`}>
                      {formData.status || 'Not set'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Start Date:</span>
                    <span className="font-medium">
                      {formData.start_date ? new Date(formData.start_date).toLocaleDateString('en-GB') : 'Not set'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">End Date:</span>
                    <span className="font-medium">
                      {formData.start_date ? new Date(calculateEndDate()).toLocaleDateString('en-GB') : 'Not set'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment:</span>
                    <span className="font-medium">
                      {formData.payment_terms || 'Not set'}
                    </span>
                  </div>
                  {!isNew && contract && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Equipment:</span>
                        <span className="font-medium">{contract.equipment?.length || 0} items</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Renewal Alert:</span>
                        <span className="text-xs">
                          {contract.renewal_alert_date
                            ? new Date(contract.renewal_alert_date).toLocaleDateString('en-GB')
                            : 'Not set'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Sites Covered */}
              {!isNew && contract && contract.equipment && contract.equipment.length > 0 && (
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Sites Covered</h3>
                  <div className="space-y-2">
                    {[...new Set(contract.equipment.map(eq => ({ id: eq.site_id, name: eq.site_name })))
                      .reduce((acc, curr) => {
                        if (!acc.find(s => s.id === curr.id)) acc.push(curr);
                        return acc;
                      }, [])]
                      .map(site => {
                        const equipmentCount = contract.equipment.filter(eq => eq.site_id === site.id).length;
                        return (
                          <div key={site.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{site.name}</div>
                              <div className="text-xs text-gray-500">{equipmentCount} {equipmentCount === 1 ? 'item' : 'items'}</div>
                            </div>
                            <button
                              onClick={() => navigate(`/sites/${site.id}`)}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              View →
                            </button>
                          </div>
                        );
                      })
                    }
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              {!isNew && (
                <div className="bg-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 text-sm">Quick Actions</h3>
                  <div className="space-y-2">
                    <button
                      disabled
                      className="w-full px-3 py-2 bg-white text-gray-400 rounded text-sm font-medium cursor-not-allowed"
                    >
                      Add Equipment (Phase 2)
                    </button>
                    <button
                      disabled
                      className="w-full px-3 py-2 bg-white text-gray-400 rounded text-sm font-medium cursor-not-allowed"
                    >
                      Generate Visits (Phase 3)
                    </button>
                    <button
                      disabled
                      className="w-full px-3 py-2 bg-white text-gray-400 rounded text-sm font-medium cursor-not-allowed"
                    >
                      Create Invoice (Phase 4)
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Template Selector Modal */}
      {showTemplateSelector && (
        <DocumentTemplateSelector
          isOpen={showTemplateSelector}
          onClose={() => setShowTemplateSelector(false)}
          documentType="ppm_contract"
          placementKey="ppm_contract.print_pdf"
          data={formData}
          onGenerate={handleTemplateGenerate}
        />
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">📧 Email PPM Contract</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="customer@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message:</label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowEmailModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="flex-1 px-4 py-2 bg-gray-300 text-black rounded hover:bg-gray-400 disabled:opacity-50"
              >
                {sendingEmail ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PPMContractDetail;
